import * as React from 'react'
import classnames from 'classnames'
import Image from 'next/image'

import { Case, ObituaryMemory as IObituaryMemory, ObituarySnippet, VisitorUser } from '../../types'
import ObituaryMemoryImage from './ObituaryMemoryImage'
import LikePopover from '../popovers/LikePopover'
import Link from 'next/link'
import { routes } from '../../routes'
import styles from './styles.module.scss'

interface IProps {
  hasReaction?: boolean
  inModal?: boolean
  obituary?: ObituarySnippet | Case
  obituaryMemory: IObituaryMemory
  onLike: (memoryID: number, newUser?: VisitorUser) => Promise<void>
  visitorUser?: VisitorUser
}

const ObituaryMemory: React.FC<IProps> = ({
  hasReaction,
  inModal,
  obituary,
  obituaryMemory,
  onLike,
  visitorUser,
}) => {
  const [isPopoverActive, setIsPopoverActive] = React.useState(false)
  const likeButtonRef = React.useRef<HTMLDivElement>()
  const footerClass = classnames(styles.footer, {
    [styles.footerNoContent]: !obituaryMemory.content,
  })
  const shouldLinkToMemory = !inModal && !!obituary
  const togglePopper = (): void => setIsPopoverActive(!isPopoverActive)
  const _onLike = async (): Promise<void> => {
    visitorUser.email ? onLike(obituaryMemory.id) : togglePopper()
  }
  const hasFlowersOrTrees = obituaryMemory.has_flowers || obituaryMemory.has_trees

  return (
    <>
      <div className={styles.wrapper}>
        <ObituaryMemoryImage
          obituary={obituary}
          obituaryMemory={obituaryMemory}
          shouldLinkToMemory={shouldLinkToMemory}
        />

        {shouldLinkToMemory ? (
          <Link href={routes.memory(obituary, obituaryMemory)}>
            <a className={styles.content}>{obituaryMemory.content}</a>
          </Link>
        ) : (
          <p className={styles.content}>{obituaryMemory.content}</p>
        )}

        <div className={footerClass}>
          {obituaryMemory.has_candle && (
            <div className={styles.candle}>
              <Image alt="Candle" layout="fixed" height={60} width={40} src="/candle.png" />
            </div>
          )}

          {hasFlowersOrTrees && (
            <div className={styles.flowerTreeWrapper}>
              {obituaryMemory.has_flowers && (
                <Image
                  alt="Flower"
                  layout="fixed"
                  height={40}
                  width={40}
                  src="/memory-flower.png"
                />
              )}

              {obituaryMemory.has_trees && (
                <Image
                  alt="Memorial Tree"
                  layout="fixed"
                  height={40}
                  width={40}
                  src="/memory-tree.png"
                />
              )}
            </div>
          )}

          <div className={styles.userInfo}>
            <p className={styles.author}>{obituaryMemory.visitor_user.name}</p>

            <p className={styles.location}>{obituaryMemory.visitor_user.location}</p>
          </div>

          <div className={styles.actions}>
            <button className={styles.actionBtn} type="button" onClick={() => _onLike()}>
              <div id={`like-btn-${obituaryMemory.id}`} ref={likeButtonRef}>
                <Image
                  alt="heart"
                  height={18}
                  layout="fixed"
                  src={hasReaction ? '/heart-active.svg' : '/heart-inactive.svg'}
                  width={20}
                />
              </div>

              <span className={styles.countText}>{obituaryMemory.like_count}</span>
            </button>

            {shouldLinkToMemory ? (
              <Link href={routes.memory(obituary, obituaryMemory)}>
                <a className={styles.actionBtn}>
                  <Image
                    alt="leave a comment"
                    height={18}
                    layout="fixed"
                    src="/message-inactive.svg"
                    width={18}
                  />

                  <span className={styles.countText}>{obituaryMemory.comments.length}</span>
                </a>
              </Link>
            ) : (
              <button className={styles.actionBtn} type="button">
                <Image
                  alt="leave a comment"
                  height={18}
                  layout="fixed"
                  src="/message-inactive.svg"
                  width={18}
                />

                <span className={styles.countText}>{obituaryMemory.comments.length}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <LikePopover
        active={isPopoverActive}
        onFormSubmit={async (values) => {
          try {
            await onLike(obituaryMemory.id, values)

            setIsPopoverActive(false)
          } catch {
            return
          }
        }}
        toggleActive={togglePopper}
        triggerElement={likeButtonRef.current}
      />
    </>
  )
}

export default ObituaryMemory
